import { useMemo, useState } from "react";

import { useBackend } from "../app/BackendProvider";
import { describeError, isOutOfScopeError, useResource } from "../app/useResource";
import { formatIsoTime, runMatchState, shortVehicleNumber } from "../app/format";
import { useFeatureFlag, useNetworkId } from "../config/ConfigProvider";
import { useActivePostParam } from "../posts/PostProvider";
import { useLiveJourneys } from "./hooks";
import { JourneyPicker } from "./JourneyPicker";
import type { ToolsScope } from "./toolsScope";
import type { Journey } from "../contract/types";

/**
 * Outil de croisement (F1.8, CDC §5.6, B5.3). Deux courses → lieu et heure
 * approximative du croisement. Calcul pur côté serveur (GET), sans effet de
 * bord. Les paramètres `vehicleA`/`vehicleB` de l'API prennent des `journeyId`
 * (le serveur croise les `nextStops` des deux courses) — une course sans
 * numéro de parc identifié reste croisable.
 *
 * Portée : « véhicule » → la course A EST le véhicule sélectionné, on ne choisit
 * que B ; « réseau » → aucun véhicule imposé, on choisit A puis B.
 */
export function CrossingPanel({ scope }: { scope: ToolsScope }) {
  const backend = useBackend();
  const networkId = useNetworkId();
  const enabled = useFeatureFlag("crossingTool");
  const postParam = useActivePostParam();

  const { items: journeys } = useLiveJourneys(postParam);
  const [pickedAId, setPickedAId] = useState<string>("");
  const [otherJourneyId, setOtherJourneyId] = useState<string>("");

  const scopedId = scope.kind === "vehicle" ? scope.journey.id : null;
  const aId = scopedId ?? pickedAId;
  const journeyA = journeys.find((j) => j.id === aId) ?? null;
  // Une course ne se croise pas avec elle-même (B gardée d'un ancien A).
  const journeyB = journeys.find((j) => j.id === otherJourneyId && j.id !== aId) ?? null;

  // La course A ne se propose pas comme course B.
  const options = useMemo(() => journeys.filter((j) => j.id !== aId), [journeys, aId]);

  const { data, error, loading } = useResource(
    (signal) =>
      enabled && journeyA && journeyB
        ? backend.api.getCrossing(networkId, journeyA.id, journeyB.id, signal)
        : Promise.resolve(null),
    [backend, networkId, enabled, journeyA?.id, journeyB?.id],
  );

  if (!enabled) return null;

  return (
    <div>
      {scopedId ? (
        journeyA ? (
          <div className="ovl" style={{ marginBottom: 6 }}>
            Course A · {courseLabel(journeyA)}
          </div>
        ) : (
          <p className="has-text-grey is-size-7">Course A hors de la liste des courses actives.</p>
        )
      ) : (
        <div style={{ marginBottom: 8 }}>
          <JourneyPicker
            label="Première course"
            placeholder="Course A : n° de véhicule, de ligne, de sortie…"
            journeys={journeys}
            selected={journeyA}
            onSelect={(j) => setPickedAId(j?.id ?? "")}
          />
        </div>
      )}
      {!journeyA ? null : (
        <>
          <div style={{ marginBottom: 10 }}>
            <JourneyPicker
              label="Seconde course"
              placeholder="Course B : n° de véhicule, de ligne, de sortie…"
              journeys={options}
              selected={journeyB}
              onSelect={(j) => setOtherJourneyId(j?.id ?? "")}
            />
          </div>

          {!journeyB ? null : loading ? (
            <p className="has-text-grey is-size-7">Calcul du croisement…</p>
          ) : isOutOfScopeError(error) ? (
            <p className="has-text-grey is-size-7">
              L'une des deux courses est hors périmètre décisionnel (information seule) — croisement non calculé.
            </p>
          ) : error ? (
            <p className="has-text-danger is-size-7">{describeError(error)}</p>
          ) : !data || data.crossing.kind === "none" ? (
            <p className="has-text-grey is-size-7">Ces deux trajets ne se croisent pas dans la fenêtre analysable.</p>
          ) : (
            <div
              style={{
                border: "1px solid var(--sentinelle-rule)",
                borderLeft: "3px solid var(--sentinelle-rail)",
                padding: "10px 12px",
                background: "var(--sentinelle-surface)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontWeight: 600, fontSize: "0.85rem", color: "var(--sentinelle-ink)" }}>
                  Croisement {data.crossing.kind === "interpolated" ? "estimé " : ""}
                  {data.crossing.atApprox ? `vers ${formatIsoTime(data.crossing.atApprox)}` : ""}
                </span>
                {/* v1.35 — fiabilité : vert = même endroit/même instant, ambre = quais
                    écartés ou Δt notable, gris = position interpolée / approchée. */}
                {data.crossing.confidence ? (
                  <span
                    className="ovl"
                    style={{
                      padding: "1px 6px",
                      borderRadius: 3,
                      color: "#fff",
                      background: CONFIDENCE[data.crossing.confidence].bg,
                    }}
                  >
                    {CONFIDENCE[data.crossing.confidence].label}
                  </span>
                ) : null}
              </div>
              <div className="ovl" style={{ marginTop: 5 }}>
                {data.crossing.stopName ??
                  (data.crossing.position
                    ? `${data.crossing.position.lat.toFixed(4)}, ${data.crossing.position.lon.toFixed(4)}`
                    : "—")}
              </div>
              {/* v1.35 — quais desservis distincts (pôle scindé / large `parent_station`) :
                  le point affiché est le MILIEU des deux quais, pas un quai précis. */}
              {data.crossing.gapMeters != null && data.crossing.gapMeters > 0 ? (
                <div className="is-size-7 has-text-grey" style={{ marginTop: 4 }}>
                  Quais distants d'environ {Math.round(data.crossing.gapMeters)} m — point pris au milieu.
                </div>
              ) : data.crossing.confidence === "low" ? (
                <div className="is-size-7 has-text-grey" style={{ marginTop: 4 }}>
                  Position approchée.
                </div>
              ) : null}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Pastille de fiabilité du croisement (v1.35). */
const CONFIDENCE: Record<"high" | "medium" | "low", { label: string; bg: string }> = {
  high: { label: "fiable", bg: "#15803d" },
  medium: { label: "quais écartés", bg: "#b45309" },
  low: { label: "approché", bg: "#6d7b8a" },
};

/** Sortie si rapprochée, sinon numéro de parc, sinon « ? » (dump utilisateur 2026-09-10). */
function courseLabel(j: Journey): string {
  const run = runMatchState(j.runId, j.runConfidence);
  if (run.matched) return run.text;
  if (j.vehicle) return shortVehicleNumber(j.vehicle.displayNumber);
  return "?";
}
